/**
 * Matrice des scores PAMI d'une campagne : un candidat par ligne, une facette
 * HEXACO ou une dimension de motivation par colonne.
 *
 * Sur `DataTable` comme les autres tables du monorepo, en largeurs `auto` :
 * une trentaine de colonnes ne se répartissent pas sur la largeur d'un écran,
 * elles défilent. La colonne Candidat reste figée, sinon une ligne de scores
 * n'appartient plus à personne passé le premier tiers du défilement.
 *
 * Ce que le passage à TanStack apporte ici : le tri par facette (« qui est le
 * plus haut en Prudence ? »), le masquage des colonnes qu'on ne regarde pas, et
 * la recherche d'un candidat — impossibles sur le `<table>` manuscrit d'avant.
 */
import {
  Card,
  CardContent,
  DataTable,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  filtreRecherche,
  type ColonneDataTable,
  type LignePied,
} from "@repo/ui";
import { useMemo } from "react";
import { HEXACO_FACTORS, MOTIVATION_DIMS } from "@repo/pami";
import { extractScoresFromResult, getScoreColor } from "./pami/pami.utils";
import type { TraitResult } from "./pami/pami.types";

interface PamiEvalRow {
  _id: string;
  parcoursId: string;
  nomCandidat?: string;
  prenomCandidat?: string;
  scoresJson?: string;
}

interface PamiCampagneTableProps {
  evaluations: PamiEvalRow[];
}

/** Une ligne de la matrice : l'évaluation, ses scores déjà décodés. */
type PamiLigne = PamiEvalRow & { scores: Record<string, number> };

const COLONNES = [
  ...HEXACO_FACTORS.flatMap((facteur) =>
    facteur.facets.map((facet) => ({
      prefix: facet.prefix,
      code: facet.code,
      label: facet.label,
    })),
  ),
  ...MOTIVATION_DIMS.map((dim) => ({
    prefix: dim.prefix,
    code: dim.prefix,
    label: dim.label,
  })),
];

const RECHERCHE = {
  colonne: "candidat",
  placeholder: "Nom, prénom…",
  libelle: "Rechercher un candidat",
} as const;

const BARRE_FILTRES = { recherche: RECHERCHE };

/** Moyenne et écart-type de population (n) sur les scores disponibles. */
function computeStats(values: number[]) {
  if (values.length === 0) return undefined;
  const moyenne = values.reduce((a, v) => a + v, 0) / values.length;
  const variance =
    values.reduce((a, v) => a + (v - moyenne) ** 2, 0) / values.length;
  return { moyenne, ecartType: Math.sqrt(variance), n: values.length };
}

/** Scores d'une colonne sur les lignes retenues, valeurs manquantes exclues. */
function scoresColonne(lignes: PamiLigne[], prefix: string): number[] {
  return lignes
    .map((ligne) => ligne.scores[prefix])
    .filter((score): score is number => score !== undefined);
}

function nomCandidat(ligne: PamiLigne): string {
  return `${ligne.prenomCandidat ?? ""} ${ligne.nomCandidat ?? ""}`.trim();
}

const colonnes: ColonneDataTable<PamiLigne>[] = [
  {
    id: "candidat",
    header: "Candidat",
    accessorFn: nomCandidat,
    cell: ({ row }) => {
      const nom = nomCandidat(row.original);
      return nom ? (
        <span className="font-medium whitespace-nowrap">{nom}</span>
      ) : (
        <span className="text-muted-foreground italic">Anonyme</span>
      );
    },
    enableHiding: false,
    filterFn: filtreRecherche<PamiLigne>((l) => [
      l.prenomCandidat,
      l.nomCandidat,
    ]),
    meta: { libelle: "Candidat", largeur: "12rem", figee: true },
  },
  ...COLONNES.map<ColonneDataTable<PamiLigne>>((col) => ({
    id: col.prefix,
    // Le code en en-tête, le libellé complet au survol : trente intitulés
    // entiers ne tiennent sur aucun écran.
    // `asChild` sur un <span> : l'en-tête triable est déjà un <button>, et un
    // bouton dans un bouton est du HTML invalide.
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="cursor-default">{col.code}</span>
        </TooltipTrigger>
        <TooltipContent>{col.label}</TooltipContent>
      </Tooltip>
    ),
    // `null` plutôt qu'`undefined` pour un score absent : TanStack range les
    // valeurs nulles en fin de tri au lieu de les mêler aux zéros.
    accessorFn: (l) => l.scores[col.prefix] ?? null,
    cell: ({ row }) => {
      const score = row.original.scores[col.prefix];
      return score === undefined ? (
        <span className="text-muted-foreground">—</span>
      ) : (
        <span
          className="inline-block rounded-pastille px-2 py-0.5 text-xs font-semibold text-surface tabular-nums"
          style={{ backgroundColor: getScoreColor(score) }}
        >
          {score}
        </span>
      );
    },
    meta: { libelle: `${col.code} — ${col.label}`, largeur: "4.5rem" },
  })),
];

/** Moyenne et écart-type, recalculés sur ce que les filtres ont retenu. */
const PIED: LignePied<PamiLigne>[] = [
  {
    libelle: (lignes) => {
      const nMax = Math.max(
        0,
        ...COLONNES.map((col) => scoresColonne(lignes, col.prefix).length),
      );
      return `Moyenne${nMax > 0 ? ` (n=${nMax})` : ""}`;
    },
    cellule: (colonneId, lignes) => {
      const stats = computeStats(scoresColonne(lignes, colonneId));
      return stats ? (
        <span className="font-semibold tabular-nums">
          {stats.moyenne.toFixed(1)}
        </span>
      ) : (
        <span className="text-muted-foreground">—</span>
      );
    },
  },
  {
    libelle: "Écart-type",
    cellule: (colonneId, lignes) => {
      const stats = computeStats(scoresColonne(lignes, colonneId));
      return (
        <span className="text-muted-foreground tabular-nums">
          {stats ? stats.ecartType.toFixed(1) : "—"}
        </span>
      );
    },
  },
];

export function PamiCampagneTable({ evaluations }: PamiCampagneTableProps) {
  // Seuls les candidats déjà scorés : une ligne de tirets ne se compare à rien.
  // Le backend renvoie aussi les passations en cours (sans `scoresJson`) parce
  // que le Matching les liste, en attente — pas cette table.
  const lignes = useMemo<PamiLigne[]>(
    () =>
      evaluations.flatMap((evaluation) => {
        if (!evaluation.scoresJson) return [];
        const scores = extractScoresFromResult(
          JSON.parse(evaluation.scoresJson) as TraitResult[],
        );
        return Object.keys(scores).length > 0 ? [{ ...evaluation, scores }] : [];
      }),
    [evaluations],
  );

  if (lignes.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">
            Aucun candidat n'a encore de score PAMI dans cette campagne.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <DataTable
      colonnes={colonnes}
      donnees={lignes}
      cleColonnes="campagne-pami"
      barreFiltres={BARRE_FILTRES}
      // Une matrice de scores se lit d'un bloc, et une moyenne calculée sur une
      // page de dix candidats ne serait la moyenne de rien.
      taillePage="toutes"
      largeurColonnes="auto"
      densite="compacte"
      pied={PIED}
      videFiltre={{ titre: "Aucun candidat pour cette recherche" }}
    />
  );
}
